import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface Account { id: string; name: string; is_active?: number }
interface SkuHealth {
  sku: string; marketplace: string; asin: string | null;
  revenue: number; units: number; cogs: number; cm2: number; cm2Pct: number;
  adSpend: number; refundRate: number; velocity: number;
  stock: number; daysOfSupply: number; salesRank: number | null;
  score: number; category: string; action: string; breakEvenAcos: number | null;
}
interface HealthData {
  days: number;
  summary: { stars: number; growth: number; marginal: number; zombies: number };
  skus: SkuHealth[];
}

const MP_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX', A1F83G8C2ARO7P: 'UK',
};

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  star: { bg: 'bg-green-100', text: 'text-green-700', label: 'Star' },
  growth: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Growth' },
  marginal: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Marginal' },
  zombie: { bg: 'bg-red-100', text: 'text-red-700', label: 'Zombie' },
};

type Period = '7' | '30' | '90';

export function FinanceHealth() {
  const [data, setData] = useState<HealthData | null>(null);
  const [period, setPeriod] = useState<Period>('30');
  const [filterCategory, setFilterCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { displayName, parentNickname } = useNicknames();

  useEffect(() => {
    (async () => {
      if (!loading) setRefreshing(true);
      try {
        const accts = await api.get<Account[]>('/accounts');
        const active = accts.filter(a => a.is_active !== 0);
        if (active.length === 0) { setLoading(false); return; }

        const results = await Promise.all(
          active.map(a => api.get<HealthData>(`/finance/sku-health/${a.id}?days=${period}`).catch(() => null)),
        );

        // Merge
        const merged: HealthData = { days: Number(period), summary: { stars: 0, growth: 0, marginal: 0, zombies: 0 }, skus: [] };
        for (const r of results) {
          if (!r) continue;
          merged.skus.push(...r.skus);
          merged.summary.stars += r.summary.stars;
          merged.summary.growth += r.summary.growth;
          merged.summary.marginal += r.summary.marginal;
          merged.summary.zombies += r.summary.zombies;
        }
        merged.skus.sort((a, b) => b.score - a.score);
        setData(merged);
      } catch { /* */ }
      setLoading(false); setRefreshing(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const sortedByParent = useMemo(() => {
    if (!data) return [];
    const groups = new Map<string, SkuHealth[]>();
    for (const s of data.skus) {
      const key = parentNickname(s.sku) ?? s.sku;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }
    const entries = [...groups.entries()];
    entries.sort((a, b) => {
      const bestA = Math.max(...a[1].map(x => x.score));
      const bestB = Math.max(...b[1].map(x => x.score));
      return bestB - bestA;
    });
    return entries.flatMap(([, items]) => items);
  }, [data, parentNickname]);

  if (loading) return <div className="py-12 text-center text-gray-400 animate-pulse">Analyzing SKU health...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No data available</div>;

  const filtered = filterCategory ? sortedByParent.filter(s => s.category === filterCategory) : sortedByParent;
  const { summary } = data;

  return (
    <div className={`space-y-6 ${refreshing ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKU Health & Lifecycle</h1>
          <p className="mt-1 text-sm text-gray-500">Composite health score — CM2, return rate, velocity, inventory, BSR</p>
        </div>
        <div className="flex gap-1">
          {(['7', '30', '90'] as Period[]).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>{p}d</button>
          ))}
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        {(['star', 'growth', 'marginal', 'zombie'] as const).map(cat => {
          const s = CATEGORY_STYLES[cat];
          const count = summary[cat === 'zombie' ? 'zombies' : cat === 'star' ? 'stars' : cat];
          return (
            <button key={cat} onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)}
              className={`rounded-xl border p-4 text-left transition-all ${
                filterCategory === cat ? `${s.bg} border-2` : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}>
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}>{s.label}</span>
                <span className="text-2xl font-bold text-gray-900">{count}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {cat === 'star' ? 'Scale & invest' : cat === 'growth' ? 'Optimize & test' : cat === 'marginal' ? 'Fix or cut' : 'Liquidate'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Product Lifecycle Scatter (simple bar representation) */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Lifecycle Map (Revenue vs Margin)</h3>
        <div className="space-y-1">
          {filtered.slice(0, 20).map(s => {
            const maxRev = Math.max(...data.skus.map(x => x.revenue));
            const revPct = maxRev > 0 ? (s.revenue / maxRev * 100) : 0;
            const style = CATEGORY_STYLES[s.category];
            return (
              <div key={`${s.sku}:${s.marketplace}`} className="flex items-center gap-2">
                <div className="w-28 text-xs text-gray-600 truncate" title={s.sku}>
                  {parentNickname(s.sku) && <span className="text-[9px] text-gray-400 block truncate">{parentNickname(s.sku)}</span>}
                  {displayName(s.sku)}
                </div>
                <span className={`w-5 text-center rounded text-[9px] font-bold ${style.bg} ${style.text}`}>{s.score}</span>
                <div className="flex-1 h-4 rounded-full bg-gray-100 overflow-hidden relative">
                  <div className={`h-full rounded-full ${style.bg.replace('100', '400')}`}
                    style={{ width: `${revPct}%` }} />
                  {s.cm2Pct !== 0 && (
                    <div className="absolute right-1 top-0 h-full flex items-center">
                      <span className={`text-[9px] font-medium ${s.cm2Pct >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {s.cm2Pct.toFixed(0)}%
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-16 text-right text-xs text-gray-500">${s.revenue.toFixed(0)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Mkt</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Score</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Revenue</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">CM2%</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">BE ACoS</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Return%</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Vel/day</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Stock</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">DoS</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(s => {
              const style = CATEGORY_STYLES[s.category];
              return (
                <tr key={`${s.sku}:${s.marketplace}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-800 max-w-[160px]" title={s.sku}>
                    {parentNickname(s.sku) && <span className="text-[10px] text-gray-400 block truncate">{parentNickname(s.sku)}</span>}
                    <span className="truncate block">{displayName(s.sku)}</span>
                  </td>
                  <td className="px-3 py-2"><span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{MP_NAMES[s.marketplace] ?? s.marketplace}</span></td>
                  <td className="px-3 py-2 text-center"><span className={`inline-block w-7 rounded text-xs font-bold ${style.bg} ${style.text}`}>{s.score}</span></td>
                  <td className="px-3 py-2 text-center"><span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${style.bg} ${style.text}`}>{style.label}</span></td>
                  <td className="px-3 py-2 text-right text-gray-700">${s.revenue.toFixed(0)}</td>
                  <td className={`px-3 py-2 text-right font-medium ${s.cm2Pct >= 15 ? 'text-green-600' : s.cm2Pct >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>{s.cm2Pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right text-blue-600 font-medium">{s.breakEvenAcos !== null ? `${s.breakEvenAcos}%` : '--'}</td>
                  <td className={`px-3 py-2 text-right ${s.refundRate > 5 ? 'text-red-600' : 'text-gray-500'}`}>{s.refundRate.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right text-gray-600">{s.velocity.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{s.stock}</td>
                  <td className={`px-3 py-2 text-right ${s.daysOfSupply < 15 ? 'text-red-600 font-medium' : s.daysOfSupply > 120 ? 'text-yellow-600' : 'text-gray-500'}`}>
                    {s.daysOfSupply > 900 ? '∞' : s.daysOfSupply}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 max-w-[180px] truncate" title={s.action}>{s.action}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
